import myAxiosRaw from './axiositeachWrapper';
import Config from '../_config/config';

// myAxios is a JS-side axios.create() instance — TS infers response types
// poorly from it (e.g. inferring the POST body as the response shape).
// Cast to any locally; we narrow via the typed wrappers below.
const myAxios: any = myAxiosRaw;

const API_BASE_URL = Config.nodeSiteUrl;

export interface PersonalizedDocumentType {
  Id: number;
  Name: string;
  IconName: string | null;
  EnableStatus: number;
  SortOrder: number;
}

export interface MatchedDocument {
  fileName: string;
  studentCode: string;
  sizeBytes: number;
  enrollmentId: number;
  studentName: string | null;
  batchName: string | null;
  willReplaceDocumentId: number | null;
  willReplaceDocumentUrl: string | null;
}

export interface UnmatchedDocument {
  fileName: string;
  sizeBytes: number;
  reason: string;
}

export interface ValidateZipResponse {
  data: {
    sessionId: string;
    matched: MatchedDocument[];
    unmatched: UnmatchedDocument[];
    totals: {
      pdfsInZip: number;
      matched: number;
      unmatched: number;
      willCreate: number;
      willReplace: number;
    };
  };
}

export interface CommitImportResponse {
  data: {
    created: number;
    replaced: number;
    failures: { enrollmentId: number; fileName: string; reason: string }[];
  };
}

export interface PersonalizedDocumentRow {
  Id: number;
  Name: string | null;
  DocumentUrl: string | null;
  PreviousDocumentUrl: string | null;
  FileName: string | null;
  MimeType: string | null;
  SizeBytes: number | null;
  Status: number;
  UploadedDate: string | null;
  UploadedByUserId: number | null;
  UploadedByName: string | null;
  DocumentTypeId: number;
  DocumentTypeName: string;
  DocumentTypeIconName: string | null;
  VisibleFrom?: string | null;
  VisibleUntil?: string | null;
  windowStatus?: 'scheduled' | 'live' | 'expired';
  StudentCode?: string | null;
  StudentName?: string | null;
  BatchId?: number | null;
  BatchName?: string | null;
  EnrollmentId?: number;
}

export interface ListAllParams {
  documentTypeId?: number | '';
  batchId?: number | '';
  studentSearch?: string;
  fromDate?: string;
  toDate?: string;
  status?: number | '';
  page?: number;
  pageSize?: number;
}

export interface ListAllResponse {
  rows: PersonalizedDocumentRow[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export async function fetchDocumentTypes(): Promise<PersonalizedDocumentType[]> {
  const res = await myAxios.get(`${API_BASE_URL}v1/personalized-documents/types`);
  return res.data?.data ?? [];
}

export async function validateZip(
  file: File,
  documentTypeId: number,
  title: string,
  onProgress?: (pct: number) => void,
): Promise<ValidateZipResponse['data']> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('documentTypeId', String(documentTypeId));
  formData.append('title', title);

  const res = await myAxios.post(
    `${API_BASE_URL}v1/personalized-documents/validate-zip`,
    formData,
    {
      onUploadProgress: (evt: ProgressEvent) => {
        if (onProgress && evt.total) {
          onProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      },
    },
  );
  return res.data?.data;
}

export async function commitImport(
  sessionId: string,
  documentTypeId: number,
  title: string,
  matched: MatchedDocument[],
  window?: { visibleFrom?: string; visibleUntil?: string },
): Promise<CommitImportResponse['data']> {
  const res = await myAxios.post(
    `${API_BASE_URL}v1/personalized-documents/commit`,
    {
      sessionId,
      documentTypeId,
      title,
      matched,
      visibleFrom: window?.visibleFrom || null,
      visibleUntil: window?.visibleUntil || null,
    },
  );
  return res.data?.data;
}

export async function updateWindow(
  ids: number[],
  window: { visibleFrom?: string; visibleUntil?: string },
): Promise<{ updated: number }> {
  const res = await myAxios.patch(
    `${API_BASE_URL}v1/personalized-documents/window`,
    {
      ids,
      visibleFrom: window.visibleFrom || null,
      visibleUntil: window.visibleUntil || null,
    },
  );
  return res.data?.data ?? { updated: 0 };
}

export async function listAll(params: ListAllParams): Promise<ListAllResponse> {
  const query: Record<string, string> = {};
  if (params.documentTypeId) query.documentTypeId = String(params.documentTypeId);
  if (params.batchId) query.batchId = String(params.batchId);
  if (params.studentSearch) query.studentSearch = params.studentSearch;
  if (params.fromDate) query.fromDate = params.fromDate;
  if (params.toDate) query.toDate = params.toDate;
  if (params.status !== undefined && params.status !== '') {
    query.status = String(params.status);
  }
  query.page = String(params.page ?? 1);
  query.pageSize = String(params.pageSize ?? 25);

  const res = await myAxios.get(
    `${API_BASE_URL}v1/personalized-documents/list`,
    { params: query },
  );
  return res.data?.data ?? { rows: [], pagination: { page: 1, pageSize: 25, total: 0, totalPages: 1 } };
}

export async function fetchDocumentsForEnrollment(
  enrollmentId: number,
): Promise<PersonalizedDocumentRow[]> {
  const res = await myAxios.get(
    `${API_BASE_URL}v1/personalized-documents/by-enrollment/${enrollmentId}`,
  );
  return res.data?.data ?? [];
}

export async function getSignedDocumentUrl(
  id: number,
  disposition: 'inline' | 'attachment' = 'inline',
): Promise<string> {
  const res = await myAxios.get(
    `${API_BASE_URL}v1/personalized-documents/${id}/signed-url?disposition=${disposition}`,
  );
  return res.data?.data?.url;
}

export async function withdrawDocument(id: number): Promise<void> {
  await myAxios.delete(`${API_BASE_URL}v1/personalized-documents/${id}`);
}
